import { useState } from 'react';
import { Button } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';
import { useProjectCommand } from '@/hooks/useProjectCommand';
import type { ProjectView } from '@/types/current-project';

export function ProjectPlanning({ project }: { project: ProjectView }) {
  const command = useProjectCommand(project.projectId);
  return <PlanningControls project={project} command={command} />;
}
export function PlanningControls({
  project,
  command,
}: {
  project: ProjectView;
  command: ReturnType<typeof useProjectCommand>;
}) {
  const account = useAuthStore((state) => state.session?.accountId);
  const [reviewedRevision, setReviewedRevision] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [cancelReviewed, setCancelReviewed] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeReviewed, setDisputeReviewed] = useState<string | null>(null);
  const disputeKey = JSON.stringify([project.planning, disputeReason]);
  const client = account === project.client;
  const coordinator = account === project.coordinator;
  const active =
    !project.cancelled &&
    !project.completed &&
    !project.planning.frozen &&
    !project.activeDisputeId;
  const locked = command.busy || command.pending || command.blocked;
  const reviewKey = JSON.stringify([
    project.planning,
    project.planning.status === 'Delivered' ? project.proposals : null,
  ]);
  const acceptance =
    client && active && project.planning.status === 'Delivered';
  const deliveryComplete = project.proposals.some(
    (p) => p.status === 'PendingApproval'
  );
  return (
    <section
      className="backendForm space-y-4 rounded-[--radi-6] border border-input bg-card p-5 text-left sm:p-8"
      aria-label="Planning actions"
    >
      <h2 className="text-xl font-semibold">Review delivered plan</h2>
      <p className="text-sm text-muted-foreground">
        Preparing the proposal has no separate planning price. Coordination is
        paid from the approved execution budget when each milestone is accepted.
      </p>
      {command.error && (
        <p role="alert">
          {command.error} Review refreshed server data before trying again.
        </p>
      )}
      {command.status && <p role="status">{command.status}</p>}
      {command.pending && (
        <div className="space-y-3">
          <p>
            A saved project request is pending. Check that exact request before
            submitting changes.
          </p>
          <Button
            disabled={command.busy || command.blocked}
            onClick={() => void command.run(null)}
          >
            Check pending project request
          </Button>
        </div>
      )}
      {acceptance && (
        <div className="space-y-4">
          <p>
            Review the proposal, milestones and attached tasks. Accepting this
            plan does not transfer funds; execution approval is a separate
            decision.
          </p>
          {project.planning.status === 'Delivered' && !deliveryComplete && (
            <p role="alert">
              The delivered plan is missing a pending proposal. Reload before
              accepting it.
            </p>
          )}
          <label className="backendCheck">
            <input
              type="checkbox"
              disabled={locked}
              checked={reviewedRevision === reviewKey}
              onChange={(event) =>
                setReviewedRevision(event.target.checked ? reviewKey : null)
              }
            />
            I reviewed planning revision {project.planning.revision}.
          </label>
          <Button
            disabled={
              locked ||
              reviewedRevision !== reviewKey ||
              (project.planning.status === 'Delivered' && !deliveryComplete)
            }
            onClick={() => {
              if (reviewedRevision !== reviewKey) return;
              void command.run({
                kind: 'accept-delivery',
                projectId: project.projectId,
                payload: { expectedRevision: project.planning.revision },
              });
              setReviewedRevision(null);
            }}
          >
            Accept delivered plan
          </Button>
        </div>
      )}
      {project.planning.status === 'Accepted' && (
        <p>
          Planning is underway. Submit the detailed proposal for client review;
          milestone tasks can be added later.
        </p>
      )}
      {project.planning.status === 'Completed' && (
        <p>
          The plan is accepted. Execution approval and funding are separate.
        </p>
      )}
      {project.planning.status === 'Disputed' && (
        <p>
          Planning is disputed. There is no public milestone case or automatic
          refund or resolution for this planning action.
        </p>
      )}
      {(client || coordinator) &&
        active &&
        ['Accepted', 'Delivered'].includes(project.planning.status) && (
          <details className="border-t border-input pt-4">
            <summary className="cursor-pointer font-semibold">
              Dispute planning
            </summary>
            <form
              className="mt-4 space-y-4"
              aria-label="Dispute planning"
              onSubmit={(event) => {
                event.preventDefault();
                if (disputeReviewed !== disputeKey) return;
                void command.run({
                  kind: 'planning-dispute',
                  projectId: project.projectId,
                  payload: { reason: disputeReason },
                });
                setDisputeReviewed(null);
              }}
            >
              <fieldset disabled={locked} className="space-y-4">
                <p>
                  This disputes the planning stage. It does not open a public
                  milestone case, refund funds or release reservations. No
                  resolution action is available.
                </p>
                <label>
                  Planning dispute reason
                  <textarea
                    required
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                  />
                </label>
                <label className="backendCheck">
                  <input
                    type="checkbox"
                    checked={disputeReviewed === disputeKey}
                    onChange={(e) =>
                      setDisputeReviewed(e.target.checked ? disputeKey : null)
                    }
                  />
                  I reviewed this planning state.
                </label>
                <Button
                  type="submit"
                  disabled={
                    disputeReviewed !== disputeKey || !disputeReason.trim()
                  }
                >
                  Confirm planning dispute
                </Button>
              </fieldset>
            </form>
          </details>
        )}
      {!client && !coordinator && (
        <p>
          Planning changes are available to the project client or coordinator as
          permitted by the server.
        </p>
      )}
      {(client || coordinator) &&
        !project.cancelled &&
        !project.completed &&
        !project.activeDisputeId && (
          <details className="border-t border-input pt-4">
            <summary className="cursor-pointer font-semibold">
              Cancel project
            </summary>
            <form
              className="mt-4 space-y-4"
              aria-label="Cancel project"
              onSubmit={(event) => {
                event.preventDefault();
                void command.run({
                  kind: 'cancel',
                  projectId: project.projectId,
                  payload: { reason },
                });
                setCancelReviewed(false);
              }}
            >
              <fieldset disabled={locked} className="space-y-4">
                <p>
                  Cancellation freezes unreleased funds. It does not
                  automatically refund escrow, reverse payments or release
                  unresolved reservations. Task writes will be blocked.
                </p>
                <label className="block">
                  Cancellation reason
                  <textarea
                    className="input w-full"
                    required
                    value={reason}
                    onChange={(event) => {
                      setReason(event.target.value);
                      setCancelReviewed(false);
                    }}
                  />
                </label>
                <label className="backendCheck">
                  <input
                    type="checkbox"
                    checked={cancelReviewed}
                    onChange={(event) =>
                      setCancelReviewed(event.target.checked)
                    }
                  />
                  I understand these consequences and want to cancel.
                </label>
                <Button
                  type="submit"
                  disabled={!cancelReviewed || !reason.trim()}
                >
                  Confirm project cancellation
                </Button>
              </fieldset>
            </form>
          </details>
        )}
    </section>
  );
}
